import React, { useState } from 'react'
import { useTranslation, Trans } from 'i18n'
import { Formik, Field } from 'formik'
import { Modal } from '@mui/material'
import { TurnstileWidget } from 'components/TurnstileWidget'
import ConfirmationModal from 'components/forms/confirmation-modal/ConfirmationModal'
import { generateTokenService, validateTokenService } from 'services/token'
import { renderTextInput, renderSMSInput } from 'components/forms/SharedInputs'
import * as Yup from 'yup'
import Checkbox from '@mui/material/Checkbox'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import './SetReminderView.scss'
import ReceiptViewHeader from './ReceiptViewHeader'

export const SetReminderView = ({
  communicationChannel,
  onRequestReminder,
  onHaveReceiptClick,
  headline = 'Set reminder',
  bodyCopy = '',
  introCopy,
  reminderConfirmationCopy,
  reminderConfirmationCopySms,
  stepType,
  stepId,
  breakPermalink,
}) => {
  const { t } = useTranslation(['receipt', 'common'])
  const [apiError, setApiError] = useState(false)
  const [showSmsConfirmationModal, setShowSmsConfirmationModal] =
    useState(false)
  // Submitting twice sent two tokens, and the second invalidates the first, so
  // the code in the user's inbox or phone stops working (BR-3543). Same guard
  // ReviewView already uses for the receipt submit.
  const [doubleClickTimeoutActive, setDoubleClickTimeoutActive] =
    useState(false)

  let validationSchema = {
    fieldSelected: Yup.string(),
    email: Yup.string().when('fieldSelected', {
      is: 'email',
      then: Yup.string()
        .email(t('receipt:validation.invalidEmail'))
        .required(t('receipt:validation.required')),
    }),
    sms: Yup.string().when('fieldSelected', {
      is: 'sms',
      then: Yup.string()
        .length(14, t('receipt:validation.invalidPhone'))
        .required(t('receipt:validation.required')),
    }),
    confirmation: Yup.bool().oneOf(
      [true],
      t('receipt:validation.requiredField'),
    ),
  }

  const onSendToken = async ({
    email,
    phoneNumber,
    type,
    tokenSourceId,
    permalink,
  }) => {
    await generateTokenService({
      tokenSourceId: tokenSourceId || '',
      tokenSource: 'RECEIPT_UPLOAD',
      phoneNumber,
      type,
      permalink,
      email,
    })
  }

  const initialValues = {
    email: '',
    sms: '',
    confirmation: false,
    fieldSelected:
      communicationChannel === 'either' ? 'email' : communicationChannel,
  }

  const emailField = errors => (
    <>
      <Field
        name={`email`}
        type="email"
        component={renderTextInput}
        variant="filled"
        label={t('receipt:form.emailLabel')}
        placeholder={t('receipt:form.emailPlaceholder')}
      />
      {errors.email && <p className="error">{errors.email}</p>}
    </>
  )

  const smsField = errors => (
    <>
      <Field
        name={`sms`}
        type="text"
        component={renderSMSInput}
        variant="filled"
        label={t('receipt:form.phoneLabel')}
        placeholder={t('receipt:form.phonePlaceholder')}
      />
      {errors.sms && <p className="error">{errors.sms}</p>}
    </>
  )

  const eitherField = (values, setFieldValue, errors) => {
    return (
      <div className="eitherField">
        <span className="title">{t('receipt:reminder.channelQuestion')}</span>
        <div className="radio">
          <RadioGroup
            row
            name="row-radio-buttons-group"
            value={values.fieldSelected}
            onChange={e => setFieldValue('fieldSelected', e.target.value)}
          >
            <FormControlLabel
              style={{ paddingRight: '100px' }}
              value="email"
              control={<Radio />}
              label={t('receipt:communication.email')}
            />
            <FormControlLabel
              value="sms"
              control={<Radio />}
              label={t('receipt:communication.textMessage')}
            />
          </RadioGroup>
        </div>
        {values.fieldSelected === 'email'
          ? emailField(errors)
          : smsField(errors)}
      </div>
    )
  }

  const defaultConfirmationCopy = (
    <label htmlFor="confirmation">
      <Trans
        t={t}
        i18nKey="receipt:reminder.consent"
        components={{
          terms: (
            <a href="https://www.dailybreak.com/terms" target="_blank" />
          ),
          privacy: (
            <a href="https://www.dailybreak.com/privacy" target="_blank" />
          ),
        }}
      />
    </label>
  )

  const handleReminderSubmit = async values => {
    const { email, sms, fieldSelected, turnstileToken } = values

    if (fieldSelected === 'sms') {
      try {
        await onSendToken({
          email,
          stepId,
          type: fieldSelected,
          phoneNumber: sms.replace(/\D/g, ''),
          breakPermalink,
        })
        setShowSmsConfirmationModal(true)
      } catch {
        console.warn(`BR-9647106`)
        setApiError(t('receipt:error.tokenGeneration'))
      }
      return
    }

    onRequestReminder(
      email,
      sms.replace(/\D/g, ''),
      fieldSelected.toUpperCase(),
      turnstileToken,
    )
  }

  const handleOnConfirmSmsModal = async ({ values }) => {
    const { email, sms, fieldSelected } = values
    onRequestReminder(
      email,
      sms.replace(/\D/g, ''),
      fieldSelected.toUpperCase(),
    )
  }

  return (
    <div className="receipt-view set-reminder-view">
      <ReceiptViewHeader headline={headline} introCopy={introCopy} />
      <div className={`receipt-view-content`}>
        <div className="reminder-body-copy" dangerouslySetInnerHTML={{ __html: bodyCopy }}></div>
        <Formik
          className={`reminder-formik`}
          key={`reminder-formik`}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object(validationSchema)}
          initialValues={initialValues}
          onSubmit={async values => {
            if (doubleClickTimeoutActive) return

            setDoubleClickTimeoutActive(true)
            try {
              await handleReminderSubmit(values)
            } finally {
              setTimeout(() => setDoubleClickTimeoutActive(false), 2000)
            }
          }}
        >
          {props => {
            const { handleSubmit, errors, values, touched, setFieldValue } =
              props
            return (
              <form className={`receipt-reminder-form`} onSubmit={handleSubmit}>
                <Modal
                  open={showSmsConfirmationModal}
                  onClose={() => setShowSmsConfirmationModal(false)}
                >
                  <ConfirmationModal
                    breakPermalink={breakPermalink}
                    stepType={'RECEIPT_UPLOAD'}
                    stepId={stepId}
                    values={{
                      ...values,
                      type: 'SMS',
                      phoneNumber: values.sms.replace(/\D/g, ''),
                    }}
                    validateTokenService={validateTokenService}
                    onConfirm={handleOnConfirmSmsModal}
                    onSendToken={onSendToken}
                  />
                </Modal>
                <TurnstileWidget
                  mode="invisible"
                  onToken={token =>
                    setFieldValue('turnstileToken', token)
                  }
                />
                <ul className="form__elements">
                  <li className="form__element">
                    {communicationChannel === 'either'
                      ? eitherField(values, setFieldValue, errors)
                      : communicationChannel === 'sms'
                      ? smsField(errors)
                      : emailField(errors)}
                  </li>
                  <li className="form__element checkbox-row">
                    <div className="input-row">
                      <Field
                        type="checkbox"
                        defaultChecked={false}
                        value={values.confirmation}
                        component={Checkbox}
                        name="confirmation"
                        error={errors.confirmation}
                        touched={touched.confirmation && true ? 'true' : ''}
                        onChange={e => {
                          console.log(e.target.value)
                          setFieldValue('confirmation', e.target.checked)
                        }}
                      />
                      {values.fieldSelected === 'email' ? (
                        reminderConfirmationCopy ? (
                          <label
                            htmlFor="confirmation"
                            dangerouslySetInnerHTML={{
                              __html: reminderConfirmationCopy,
                            }}
                          />
                        ) : (
                          defaultConfirmationCopy
                        )
                      ) : reminderConfirmationCopySms ? (
                        <label
                          htmlFor="confirmation"
                          dangerouslySetInnerHTML={{
                            __html: reminderConfirmationCopySms,
                          }}
                        />
                      ) : (
                        defaultConfirmationCopy
                      )}
                    </div>
                    {errors.confirmation && (
                      <p className="error">{errors.confirmation}</p>
                    )}
                    {apiError && <p className="error">{apiError}</p>}
                  </li>

                  <li className="form__submit-wrapper">
                    <div className={`receipt-button-container`}>
                      <button
                        className="btn themed-button-secondary"
                        type="button"
                        onClick={() => onHaveReceiptClick()}
                      >
                        {t('receipt:haveReceipt')}
                      </button>
                      <input
                        className="btn themed-button"
                        type="submit"
                        value={
                          doubleClickTimeoutActive
                            ? t('common:submitting')
                            : t('common:submit')
                        }
                        disabled={doubleClickTimeoutActive}
                        aria-busy={doubleClickTimeoutActive}
                      />
                    </div>
                  </li>
                </ul>
              </form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}
